import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { jsPDF } from 'jspdf';
import mammoth from 'mammoth';
import { PDFParse } from 'pdf-parse';
import './CheckerMain.css';

// Configure PDF.js worker
PDFParse.setWorker('https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs');

const MAX_PAGES = 10;
const CRAWL_TIMEOUT = 120000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const AHPRA_GUIDELINES = `OFFICIAL AHPRA GUIDELINES from "Guidelines for advertising a regulated health service" (December 2020):

SECTION 133 OF THE NATIONAL LAW - A person must not advertise in a way that:
(a) is false, misleading or deceptive or is likely to be misleading or deceptive
(b) offers a gift, discount or other inducement, unless the advertisement also states the terms and conditions
(c) uses testimonials or purported testimonials about the service or business
(d) creates an unreasonable expectation of beneficial treatment
(e) directly or indirectly encourages the indiscriminate or unnecessary use of regulated health services

4.3 TESTIMONIALS: "Testimonials are recommendations or positive statements about the clinical aspects of a regulated health service used in advertising. A clinical aspect exists if one of the following is expressed: Symptom, Diagnosis or treatment, Outcome. Not all reviews or positive comments are testimonials - comments about customer service or communication style that do not include clinical aspects are not considered testimonials."

4.4 UNREASONABLE EXPECTATIONS: "Advertising may breach when it: creates unreasonable expectation of outcomes or recovery time, overstates potential benefit, minimises complexity of risk (using words like 'safe', 'effective', 'risk-free', 'pain-free' without acknowledging possible adverse reactions), contains claim that treatment is infallible, unfailing, magical, miraculous, certainty, guaranteed or sure cure, states practitioner has exclusive or unique skill or remedy."

4.1.4.b OTHER USES OF SPECIALISATIONS: "When a practitioner does not hold specialist registration, any advertising using words or titles related to specialty is likely to mislead the public. This includes 'specialist', 'specialises in', 'specialty', or 'specialised'. Words such as 'substantial experience in' or 'working primarily in' are less likely to be misleading."

4.1.4.d QUALIFICATIONS: "If a practitioner holds further or postgraduate qualifications, specific experience, or completed specific courses it is acceptable to advertise that in an accurate and factual manner."

4.2 GIFTS, DISCOUNTS OR INDUCEMENTS: "Advertising that offers gift, discount or inducement must state the terms and conditions. The public generally consider 'free' to mean 'absolutely' free. When costs are recouped through price rise elsewhere or through Medicare, the offer is not actually free."

4.5 ENCOURAGING UNNECESSARY USE: "Advertising may be unlawful when it: Creates impression of urgency linked to health suffering ('don't delay', 'act now', 'time running out'), encourages periodic appointments where there is no clinical indication, uses incentives that would encourage people to use services regardless of clinical need."`;

function CheckerMain({
  user,
  hasActiveSubscription,
  subscriptionTier,
  monthlyChecksUsed,
  monthlyCheckLimit,
  checksRemaining,
  canPerformCheck,
  onCheckPerformed,
  onSaveCheck,
  selectedCheck,
  onNewCheck
}) {
  const [textInput, setTextInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [showUrlOptions, setShowUrlOptions] = useState(false);
  const [crawlMode, setCrawlMode] = useState('single');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isPageDragging, setIsPageDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState(null);
  const [crawlProgress, setCrawlProgress] = useState(null);
  const [error, setError] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const [messages, setMessages] = useState([]);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [emailingResults, setEmailingResults] = useState(false);
  const [copiedSuggestion, setCopiedSuggestion] = useState(null);

  const handleCopySuggestion = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSuggestion(id);
      setTimeout(() => setCopiedSuggestion(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const [emailSent, setEmailSent] = useState(false);

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle document passed from Document Repository via route state
  useEffect(() => {
    const fromDocument = location.state?.fromDocument;
    if (!fromDocument) return;

    // Clear route state so it doesn't re-trigger on subsequent renders
    window.history.replaceState({}, '', location.pathname);

    const loadDocumentFromStorage = async () => {
      setLoading(true);
      setError(null);
      setAnalysisPhase('processing');

      try {
        // Get a signed URL via the server-side API (uses service role key for storage access)
        const session = await supabase.auth.getSession();
        const token = session.data?.session?.access_token;

        const urlRes = await fetch(`/api/documents-download?documentId=${fromDocument.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!urlRes.ok) {
          throw new Error('Failed to get download URL');
        }

        const { url: signedUrl } = await urlRes.json();

        // Fetch the actual file binary from the signed URL
        const fileRes = await fetch(signedUrl);
        if (!fileRes.ok) {
          throw new Error('Failed to download file');
        }

        const arrayBuffer = await fileRes.arrayBuffer();
        const file = new File([arrayBuffer], fromDocument.filename, { type: fromDocument.mimeType });

        // Reset loading state — processUploadedFile will set its own
        setLoading(false);
        setAnalysisPhase(null);

        // Feed into the existing file processing pipeline
        await processUploadedFile(file);
      } catch (err) {
        setError(err.message || 'Failed to load document from repository');
        setLoading(false);
        setAnalysisPhase(null);
      }
    };

    loadDocumentFromStorage();
  }, [location.state]); // eslint-disable-line

  // Auto-resize textarea
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  // Load selected check results if viewing history
  useEffect(() => {
    if (selectedCheck) {
      setMessages([
        { type: 'user', content: selectedCheck },
        { type: 'assistant', content: selectedCheck.results }
      ]);
    } else {
      setMessages([]);
    }
  }, [selectedCheck]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // URL validation and option display
  useEffect(() => {
    if (urlInput.trim()) {
      try {
        new URL(urlInput.startsWith('http') ? urlInput : `https://${urlInput}`);
        setShowUrlOptions(true);
      } catch {
        setShowUrlOptions(false);
      }
    } else {
      setShowUrlOptions(false);
    }
  }, [urlInput]);

  // Helper to detect if text looks like a URL
  const looksLikeUrl = (text) => {
    const trimmed = text.trim();
    // Check for common URL patterns
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return true;
    }
    // Check for www. prefix
    if (trimmed.startsWith('www.')) {
      return true;
    }
    // Check for domain-like pattern (e.g., example.com, example.com.au)
    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+([\/\?#].*)?$/;
    if (domainPattern.test(trimmed) && !trimmed.includes(' ') && trimmed.length < 200) {
      return true;
    }
    return false;
  };


  // Page-level drag and drop for files
  const handlePageDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;

    // Check if dragging files
    if (e.dataTransfer.types.includes('Files')) {
      setIsPageDragging(true);
    }
  };

  const handlePageDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;

    if (dragCounterRef.current === 0) {
      setIsPageDragging(false);
    }
  };

  const handlePageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handlePageDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPageDragging(false);
    dragCounterRef.current = 0;

    if (e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      const ext = file.name.split('.').pop().toLowerCase();

      // Only handle supported file types
      if (['pdf', 'docx', 'txt'].includes(ext)) {
        // Set the file and immediately process it
        setUploadedFile(file);
        // Auto-process the dropped file
        await processUploadedFile(file);
      } else {
        setError('Unsupported file type. Please use PDF, DOCX, or TXT');
      }
    }
  };

  // Process uploaded file (extracted for reuse)
  const processUploadedFile = async (file) => {
    if (!file) return;

    if (!canPerformCheck) {
      setShowUpgradePrompt(true);
      return;
    }

    // Start a new check session if there are existing messages
    if (messages.length > 0 && onNewCheck) {
      onNewCheck();
    }

    setLoading(true);
    setError(null);
    setAnalysisPhase('processing');

    const userMessage = {
      type: 'user',
      content: {
        inputType: 'file',
        inputSource: file.name
      }
    };

    // Start fresh with just this message
    setMessages([userMessage]);

    try {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      let extractedText = '';

      if (fileExtension === 'pdf') {
        extractedText = await extractTextFromPDF(file);
      } else if (fileExtension === 'docx') {
        extractedText = await extractTextFromDOCX(file);
      } else if (fileExtension === 'txt') {
        extractedText = await extractTextFromTXT(file);
      }

      if (!extractedText || extractedText.length < 50) {
        throw new Error('Could not extract sufficient text from the file');
      }

      setAnalysisPhase('analyzing');

      const analysis = await checkComplianceWithText(extractedText);

      const assistantMessage = {
        type: 'assistant',
        content: analysis
      };

      setMessages(prev => [...prev, assistantMessage]);

      onSaveCheck({
        inputType: 'file',
        inputSource: file.name,
        inputPreview: extractedText.substring(0, 200),
        results: analysis
      });

      if (subscriptionTier !== 'pro' && onCheckPerformed) {
        onCheckPerformed();
      }

      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      setError(err.message || 'Failed to process file');
    } finally {
      setLoading(false);
      setAnalysisPhase(null);
    }
  };

  const checkComplianceWithText = async (text, { skipUsageRecord = false } = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch('/api/check-compliance', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        content: `Analyze this advertising text for AHPRA compliance:\n\n${text}`,
        skipUsageRecord
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 429) {
        throw new Error(errorData.error || 'Rate limit exceeded. Please try again later.');
      }
      throw new Error(errorData.error || 'Analysis failed. Please try again.');
    }

    const data = await response.json();
    let responseText = data.result;

    // Clean up response - remove markdown code blocks if present
    responseText = responseText.trim();
    if (responseText.startsWith('```json')) {
      responseText = responseText.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (responseText.startsWith('```')) {
      responseText = responseText.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }

    // Parse the JSON array
    let violations;
    try {
      violations = JSON.parse(responseText);
    } catch (error) {
      console.error('Failed to parse AI response:', error, responseText.substring(0, 200));
      throw new Error('Failed to parse compliance check results. Please try again.');
    }

    // Validate it's an array
    if (!Array.isArray(violations)) {
      console.error('AI response is not an array:', responseText.substring(0, 200));
      throw new Error('Invalid response format from AI. Please try again.');
    }

    // Filter out malformed violations
    const validViolations = violations.filter(v => {
      if (!v.category || !v.problem || !v.explanation || !v.fix) return false;
      if (v.category.toLowerCase().includes('extracted from analysis')) return false;
      if (v.problem.includes('{') || v.problem.includes('[')) return false;
      if (v.fix.toLowerCase().includes('review') && v.fix.length < 20) return false;
      if (v.problem.length < 10) return false;
      return true;
    });

    // Convert to the format expected by the UI
    const issues = validViolations.map(v => ({
      problematicText: v.problem,
      rule: v.category,
      explanation: v.explanation,
      suggestion: v.fix
    }));

    return {
      compliant: issues.length === 0,
      issues,
      summary: issues.length === 0 ? 'No AHPRA violations found.' : `Found ${issues.length} potential AHPRA violation${issues.length > 1 ? 's' : ''}.`
    };
  };

  const handleTextSubmit = async () => {
    if (!textInput.trim()) {
      setError('Please enter some text to check');
      return;
    }

    if (!canPerformCheck) {
      setShowUpgradePrompt(true);
      return;
    }

    // Start a new check session if there are existing messages
    if (messages.length > 0 && onNewCheck) {
      onNewCheck();
    }

    setLoading(true);
    setError(null);
    setAnalysisPhase('analyzing');

    const userMessage = {
      type: 'user',
      content: {
        inputType: 'text',
        inputPreview: textInput.substring(0, 200),
        text: textInput
      }
    };

    // Start fresh with just this message
    setMessages([userMessage]);

    try {
      const analysis = await checkComplianceWithText(textInput);

      const assistantMessage = {
        type: 'assistant',
        content: analysis
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save to history
      onSaveCheck({
        inputType: 'text',
        inputPreview: textInput.substring(0, 200),
        results: analysis
      });

      if (subscriptionTier !== 'pro' && onCheckPerformed) {
        onCheckPerformed();
      }

      setTextInput('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      setError(err.message || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
      setAnalysisPhase(null);
    }
  };

  const handleUrlSubmit = async (mode) => {
    let url = urlInput.trim();
    if (!url.startsWith('http')) {
      url = `https://${url}`;
    }

    if (!canPerformCheck) {
      setShowUpgradePrompt(true);
      return;
    }

    // Start a new check session if there are existing messages
    if (messages.length > 0 && onNewCheck) {
      onNewCheck();
    }

    setLoading(true);
    setError(null);
    setCrawlMode(mode);

    const userMessage = {
      type: 'user',
      content: {
        inputType: 'url',
        inputSource: url,
        isSiteCrawl: mode === 'entire'
      }
    };

    // Start fresh with just this message
    setMessages([userMessage]);

    try {
      if (mode === 'single') {
        setAnalysisPhase('connecting');

        // Use the check-url endpoint to extract text
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const scrapeResponse = await fetch('/api/check-url', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authSession?.access_token}`
          },
          body: JSON.stringify({ url })
        });

        if (!scrapeResponse.ok) {
          const errorData = await scrapeResponse.json().catch(() => ({}));
          if (scrapeResponse.status === 429) {
            throw new Error(errorData.error || 'Rate limit exceeded. Please try again later.');
          }
          throw new Error(errorData.error || 'Failed to fetch website content');
        }

        const { text } = await scrapeResponse.json();

        if (!text || text.trim().length < 100) {
          throw new Error('Failed to extract sufficient content from URL.');
        }

        setAnalysisPhase('analyzing');

        const analysis = await checkComplianceWithText(text);

        const assistantMessage = {
          type: 'assistant',
          content: analysis
        };

        setMessages(prev => [...prev, assistantMessage]);

        onSaveCheck({
          inputType: 'url',
          inputSource: url,
          inputPreview: url,
          results: analysis
        });

      } else {
        // Site crawl
        setAnalysisPhase('connecting');
        setCrawlProgress({ current: 0, total: 0, analyzed: 0 });

        abortControllerRef.current = new AbortController();

        // Use check-url with crawlSite=true to discover pages
        const { data: { session: crawlAuthSession } } = await supabase.auth.getSession();
        const crawlResponse = await fetch('/api/check-url', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${crawlAuthSession?.access_token}`
          },
          body: JSON.stringify({ url, crawlSite: true }),
          signal: abortControllerRef.current.signal
        });

        if (!crawlResponse.ok) {
          const errorData = await crawlResponse.json().catch(() => ({}));
          if (crawlResponse.status === 429) {
            throw new Error(errorData.error || 'Rate limit exceeded. Please try again later.');
          }
          throw new Error('Failed to crawl website');
        }

        const crawlData = await crawlResponse.json();
        const pageUrls = crawlData.urls || [url];
        const pagesToAnalyze = pageUrls.slice(0, MAX_PAGES);

        setCrawlProgress({ current: 0, total: pagesToAnalyze.length, analyzed: 0 });
        setAnalysisPhase('analyzing');

        const pageResults = [];
        for (let i = 0; i < pagesToAnalyze.length; i++) {
          setCrawlProgress(prev => ({ ...prev, current: i + 1 }));

          try {
            // Fetch text for each page
            const { data: { session: pageAuthSession } } = await supabase.auth.getSession();
            const pageResponse = await fetch('/api/check-url', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${pageAuthSession?.access_token}`
              },
              body: JSON.stringify({ url: pagesToAnalyze[i] })
            });

            if (!pageResponse.ok) {
              throw new Error('Failed to fetch page');
            }

            const pageData = await pageResponse.json();
            const analysis = await checkComplianceWithText(pageData.text, { skipUsageRecord: true });

            pageResults.push({
              url: pagesToAnalyze[i],
              analysis
            });
          } catch (err) {
            pageResults.push({
              url: pagesToAnalyze[i],
              error: err.message
            });
          }
        }

        const siteAnalysis = {
          isSiteAnalysis: true,
          analyzedPages: pageResults.length,
          totalViolations: pageResults.reduce((sum, p) => sum + (p.analysis?.issues?.length || 0), 0),
          pagesWithViolations: pageResults.filter(p => p.analysis?.issues?.length > 0).length,
          pages: pageResults
        };

        const assistantMessage = {
          type: 'assistant',
          content: siteAnalysis
        };

        setMessages(prev => [...prev, assistantMessage]);

        onSaveCheck({
          inputType: 'url',
          inputSource: url,
          inputPreview: url,
          isSiteCrawl: true,
          pagesAnalyzed: pageResults.length,
          pagesWithViolations: siteAnalysis.pagesWithViolations,
          results: siteAnalysis
        });
      }

      if (subscriptionTier !== 'pro' && onCheckPerformed) {
        onCheckPerformed();
      }

      setUrlInput('');
      setShowUrlOptions(false);
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Analysis failed. Please try again.');
      }
    } finally {
      setLoading(false);
      setAnalysisPhase(null);
      setCrawlProgress(null);
    }
  };

  const extractTextFromPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfParser = new PDFParse({ data: new Uint8Array(arrayBuffer) });
    const result = await pdfParser.getText();
    return result.text.trim();
  };

  const extractTextFromDOCX = async (file) => {
    const arrayBuffer = await file.arrayBuffer();

    // Check for ZIP signature (DOCX files are ZIP archives)
    // If not a valid ZIP, it may be a plain text file with a .docx extension
    const header = new Uint8Array(arrayBuffer.slice(0, 4));
    if (header[0] !== 0x50 || header[1] !== 0x4B || header[2] !== 0x03 || header[3] !== 0x04) {
      const decoder = new TextDecoder('utf-8');
      return decoder.decode(arrayBuffer).trim();
    }

    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value.trim();
  };

  const extractTextFromTXT = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };


  const handleFileSelect = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 10MB limit');
      return;
    }

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'txt'].includes(ext)) {
      setError('Unsupported file type. Please use PDF, DOCX, or TXT');
      return;
    }

    setUploadedFile(file);
    setError(null);
  };

  const getRiskLevel = (issue) => {
    const rule = issue.rule?.toLowerCase() || '';
    if (rule.includes('testimonial') || rule.includes('guarantee') || rule.includes('specialist')) {
      return 'high';
    }
    if (rule.includes('unreasonable') || rule.includes('misleading')) {
      return 'medium';
    }
    return 'low';
  };

  const generatePDF = (results) => {
    setGeneratingPDF(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      const addText = (text, fontSize, isBold = false) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += fontSize * 0.5;
        });
        yPosition += 4;
      };

      addText('Mindmark Compliance Report', 18, true);
      addText(`Generated: ${new Date().toLocaleDateString()}`, 10);
      yPosition += 10;

      if (results.isSiteAnalysis) {
        addText(`Pages Analyzed: ${results.analyzedPages}`, 12);
        addText(`Total Violations: ${results.totalViolations}`, 12);
        yPosition += 10;

        results.pages?.forEach(page => {
          addText(`Page: ${page.url}`, 12, true);
          if (page.analysis?.issues?.length > 0) {
            page.analysis.issues.forEach((issue, i) => {
              addText(`${i + 1}. ${issue.rule}`, 11, true);
              addText(`Problem: "${issue.problematicText}"`, 10);
              addText(`Suggestion: ${issue.suggestion}`, 10);
            });
          } else {
            addText('No violations found', 10);
          }
          yPosition += 8;
        });
      } else {
        if (results.issues?.length > 0) {
          addText(`Found ${results.issues.length} potential violation(s)`, 12);
          yPosition += 10;

          results.issues.forEach((issue, i) => {
            addText(`${i + 1}. ${issue.rule}`, 11, true);
            addText(`Problem: "${issue.problematicText}"`, 10);
            addText(`Explanation: ${issue.explanation}`, 10);
            addText(`Suggestion: ${issue.suggestion}`, 10);
            yPosition += 6;
          });
        } else {
          addText('No violations found. Content appears to be AHPRA compliant.', 12);
        }
      }

      doc.save('mindmark-report.pdf');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const emailResults = async (results) => {
    if (!user?.email) {
      setError('No email address found');
      return;
    }

    setEmailingResults(true);
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: user.email,
          type: 'check_results',
          results
        })
      });

      if (!response.ok) throw new Error('Failed to send email');

      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
    } catch (err) {
      setError('Failed to send email');
    } finally {
      setEmailingResults(false);
    }
  };

  const renderUserMessage = (msg) => {
    const { content } = msg;
    return (
      <div className="message user-message">
        <div className="message-bubble user-bubble">
          {content.inputType === 'text' && (
            <>
              <span className="message-label">Check text:</span>
              <p className="message-preview">"{content.inputPreview}..."</p>
            </>
          )}
          {content.inputType === 'url' && (
            <>
              <span className="message-label">
                {content.isSiteCrawl ? 'Check entire site:' : 'Check page:'}
              </span>
              <p className="message-url">{content.inputSource}</p>
            </>
          )}
          {content.inputType === 'file' && (
            <>
              <span className="message-label">Check file:</span>
              <p className="message-file">{content.inputSource}</p>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderAssistantMessage = (msg) => {
    const results = msg.content;

    if (results.isSiteAnalysis) {
      return (
        <div className="message assistant-message">
          <div className="results-header">
            <h3>Compliance Check Results</h3>
            <p className="results-summary">
              Analyzed {results.analyzedPages} pages. Found {results.totalViolations} potential issue(s) on {results.pagesWithViolations} page(s).
            </p>
          </div>

          {results.pages?.map((page, pageIndex) => (
            <div key={pageIndex} className="page-result">
              <div className="page-header">
                <span className="page-url">{page.url}</span>
                {page.analysis?.issues?.length > 0 ? (
                  <span className="issue-count">{page.analysis.issues.length} issues</span>
                ) : (
                  <span className="compliant-badge">Compliant</span>
                )}
              </div>

              {page.analysis?.issues?.map((issue, issueIndex) => (
                <div key={issueIndex} className={`issue-card risk-${getRiskLevel(issue)}`}>
                  <div className="issue-header">
                    <span className={`risk-badge ${getRiskLevel(issue)}`}>
                      {getRiskLevel(issue) === 'high' ? 'High Risk' :
                       getRiskLevel(issue) === 'medium' ? 'Medium Risk' : 'Low Risk'}
                    </span>
                    <span className="issue-rule">{issue.rule}</span>
                  </div>
                  <p className="issue-text">
                    <strong>Problem:</strong> "{issue.problematicText}"
                  </p>
                  <p className="issue-explanation">{issue.explanation}</p>
                  <div className="issue-suggestion">
                    <div className="suggestion-text">
                      <strong>Suggestion:</strong> {issue.suggestion}
                    </div>
                    <button
                      className={`copy-suggestion-btn ${copiedSuggestion === `url-${pageIndex}-${issueIndex}` ? 'copied' : ''}`}
                      onClick={() => handleCopySuggestion(issue.suggestion, `url-${pageIndex}-${issueIndex}`)}
                      title="Copy suggestion"
                    >
                      {copiedSuggestion === `url-${pageIndex}-${issueIndex}` ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div className="results-actions">
            <button
              className="action-btn"
              onClick={() => generatePDF(results)}
              disabled={generatingPDF}
            >
              {generatingPDF ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              className="action-btn"
              onClick={() => emailResults(results)}
              disabled={emailingResults}
            >
              {emailSent ? 'Sent!' : emailingResults ? 'Sending...' : 'Email Results'}
            </button>
          </div>
        </div>
      );
    }

    // Single check results
    const issues = results.issues || [];
    const isCompliant = issues.length === 0;

    return (
      <div className="message assistant-message">
        <div className="results-header">
          <h3>Compliance Check Results</h3>
          {isCompliant ? (
            <div className="compliant-message">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <p>No violations found. Content appears to be AHPRA compliant.</p>
            </div>
          ) : (
            <p className="results-summary">Found {issues.length} potential issue(s):</p>
          )}
        </div>

        {issues.map((issue, index) => (
          <div key={index} className={`issue-card risk-${getRiskLevel(issue)}`}>
            <div className="issue-header">
              <span className={`risk-badge ${getRiskLevel(issue)}`}>
                {getRiskLevel(issue) === 'high' ? 'High Risk' :
                 getRiskLevel(issue) === 'medium' ? 'Medium Risk' : 'Low Risk'}
              </span>
              <span className="issue-rule">{issue.rule}</span>
            </div>
            <p className="issue-text">
              <strong>Problem:</strong> "{issue.problematicText}"
            </p>
            <p className="issue-explanation">{issue.explanation}</p>
            <div className="issue-suggestion">
              <div className="suggestion-text">
                <strong>Suggestion:</strong> {issue.suggestion}
              </div>
              <button
                className={`copy-suggestion-btn ${copiedSuggestion === `text-${index}` ? 'copied' : ''}`}
                onClick={() => handleCopySuggestion(issue.suggestion, `text-${index}`)}
                title="Copy suggestion"
              >
                {copiedSuggestion === `text-${index}` ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        ))}

        <div className="results-actions">
          <button
            className="action-btn"
            onClick={() => generatePDF(results)}
            disabled={generatingPDF}
          >
            {generatingPDF ? 'Generating...' : 'Download PDF'}
          </button>
          <button
            className="action-btn"
            onClick={() => emailResults(results)}
            disabled={emailingResults}
          >
            {emailSent ? 'Sent!' : emailingResults ? 'Sending...' : 'Email Results'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className="checker-main"
      onDragEnter={handlePageDragEnter}
      onDragLeave={handlePageDragLeave}
      onDragOver={handlePageDragOver}
      onDrop={handlePageDrop}
    >
      {/* Page-level drop overlay */}
      {isPageDragging && (
        <div className="page-drop-overlay">
          <div className="drop-overlay-content">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <p>Drop file to check</p>
            <span>PDF, DOCX, or TXT</span>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="messages-area">
        {messages.length === 0 && !loading ? (
          <div className="empty-state">
            <h2>What would you like to check?</h2>
            <p>Paste text, enter a URL, or upload a document to check for AHPRA compliance.</p>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((msg, index) => (
              <React.Fragment key={index}>
                {msg.type === 'user' ? renderUserMessage(msg) : renderAssistantMessage(msg)}
              </React.Fragment>
            ))}

            {loading && (
              <div className="message assistant-message loading-message">
                <div className="loading-indicator">
                  <div className="spinner"></div>
                  <span>
                    {analysisPhase === 'connecting' && 'Connecting to website...'}
                    {analysisPhase === 'analyzing' && (crawlProgress
                      ? `Analyzing page ${crawlProgress.current} of ${crawlProgress.total}...`
                      : 'Analyzing content...'
                    )}
                    {analysisPhase === 'processing' && 'Processing file...'}
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="input-separator" />
      <div className="input-area">
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {showUpgradePrompt && (
          <div className="upgrade-prompt">
            <div className="upgrade-prompt-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="32" height="32">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <h3 className="upgrade-prompt-title">Monthly Check Limit Reached</h3>
            <p className="upgrade-prompt-text">
              {subscriptionTier === 'free'
                ? `You've used all ${monthlyCheckLimit} free checks this month. Upgrade to Basic for 50 checks/month or Pro for unlimited.`
                : `You've used all ${monthlyCheckLimit} checks this month. Upgrade to Pro for unlimited checks.`
              }
            </p>
            <button className="btn-primary upgrade-prompt-btn" onClick={() => navigate('/pricing')}>
              {subscriptionTier === 'free' ? 'View Plans' : 'Upgrade to Pro'}
            </button>
            <button className="btn-ghost upgrade-prompt-dismiss" onClick={() => setShowUpgradePrompt(false)}>
              Dismiss
            </button>
          </div>
        )}

        {/* Unified Input */}
        <div className="input-content">
          <div className="unified-input-wrapper">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={async (e) => {
                if (e.target.files[0]) {
                  const file = e.target.files[0];
                  handleFileSelect(file);
                  // Auto-process the file after selection
                  await processUploadedFile(file);
                }
              }}
              accept=".pdf,.docx,.txt"
              hidden
            />

            {/* Add file button */}
            <button
              className="add-file-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              title="Add files to check"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>

            {/* Main textarea */}
            <textarea
              ref={textareaRef}
              className="unified-input"
              placeholder="Paste text or a URL to check for AHPRA compliance..."
              value={textInput || urlInput}
              onChange={(e) => {
                const value = e.target.value;
                const currentValue = textInput || urlInput;

                // If there are existing messages (completed check) and user starts typing
                // from an empty input, automatically start a new check session
                if (messages.length > 0 && value.trim() && !currentValue.trim()) {
                  setMessages([]);
                  if (onNewCheck) {
                    onNewCheck();
                  }
                }

                // Auto-detect if it's a URL or text
                if (looksLikeUrl(value.trim())) {
                  setUrlInput(value);
                  setTextInput('');
                } else {
                  setTextInput(value);
                  setUrlInput('');
                }
                // Auto-resize
                autoResizeTextarea();
              }}
              onKeyDown={(e) => {
                // Submit on Enter (without Shift)
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const value = (textInput || urlInput).trim();
                  if (value && !loading) {
                    if (looksLikeUrl(value)) {
                      // For URLs, show the options popup
                      setUrlInput(value);
                      setTextInput('');
                    } else {
                      handleTextSubmit();
                    }
                  }
                }
              }}
              disabled={loading}
              rows={1}
            />

            {/* Submit button */}
            <button
              className="submit-btn"
              onClick={() => {
                const value = (textInput || urlInput).trim();
                if (looksLikeUrl(value)) {
                  // URL is already set, options will show
                  setUrlInput(value);
                  setTextInput('');
                } else {
                  handleTextSubmit();
                }
              }}
              disabled={loading || (!textInput.trim() && !urlInput.trim())}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>

          {/* URL options popup */}
          {showUrlOptions && urlInput.trim() && (
            <div className="url-options">
              <p>Would you like to check:</p>
              <div className="url-option-buttons">
                <button
                  className="url-option"
                  onClick={() => handleUrlSubmit('single')}
                  disabled={loading}
                >
                  Just this page
                </button>
                <button
                  className="url-option"
                  onClick={() => handleUrlSubmit('entire')}
                  disabled={loading}
                >
                  Entire site (up to {MAX_PAGES} pages)
                </button>
              </div>
            </div>
          )}

          {/* Uploaded file indicator */}
          {uploadedFile && (
            <div className="uploaded-file-indicator">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <span>{uploadedFile.name}</span>
              <button
                className="remove-file-btn"
                onClick={() => {
                  setUploadedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CheckerMain;
